/**
 * Timezone-aware helpers for admin datetime-local inputs (Bug X1).
 *
 * `<input type="datetime-local">` reads/writes in the BROWSER's local zone,
 * which is wrong for events tied to a specific IANA timezone. If an admin in
 * Eastern types "6:00 PM" for a Chicago event, we want to save 6:00 PM CENTRAL
 * (not 6:00 PM Eastern rendered as 5:00 PM Central).
 *
 * These two helpers convert between:
 *   - "YYYY-MM-DDTHH:MM"  wall-clock in the EVENT's IANA zone  (the input value)
 *   - a full UTC ISO string                                    (what we store)
 *
 * If `tz` is falsy / invalid we transparently fall back to the browser's local
 * zone, matching the previous behavior for forms that opted in later.
 */

const isValidTz = (tz) => {
  if (!tz || typeof tz !== 'string') return false;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: tz }).format(new Date());
    return true;
  } catch { return false; }
};

/** UTC ISO -> "YYYY-MM-DDTHH:MM" wall-clock in the event's tz. */
export const utcToEventLocal = (iso, tz) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  if (!isValidTz(tz)) {
    // Fallback: browser local (old behavior).
    const off = d.getTimezoneOffset();
    return new Date(d.getTime() - off * 60000).toISOString().slice(0, 16);
  }
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hour12: false,
  }).formatToParts(d);
  const g = (t) => parts.find((p) => p.type === t)?.value || '';
  let hh = g('hour');
  if (hh === '24') hh = '00';   // Intl edge case at midnight in some engines
  return `${g('year')}-${g('month')}-${g('day')}T${hh}:${g('minute')}`;
};

/** "YYYY-MM-DDTHH:MM" wall-clock in the event's tz -> UTC ISO string. */
export const eventLocalToUtc = (s, tz) => {
  if (!s) return null;
  if (!isValidTz(tz)) {
    // Fallback: interpret in browser local (old behavior).
    const d = new Date(s);
    return Number.isNaN(d.getTime()) ? null : d.toISOString();
  }
  // Interpret the wall-clock as if it were UTC, then correct for the tz's
  // offset AT THAT INSTANT (accounting for DST).
  const asUtc = new Date(`${s}:00Z`);
  if (Number.isNaN(asUtc.getTime())) return null;
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
  }).formatToParts(asUtc);
  const g = (t) => parts.find((p) => p.type === t)?.value || '00';
  const asIfLocal = Date.UTC(
    +g('year'), +g('month') - 1, +g('day'),
    +g('hour') === 24 ? 0 : +g('hour'),
    +g('minute'), +g('second'),
  );
  const offset = asIfLocal - asUtc.getTime();
  return new Date(asUtc.getTime() - offset).toISOString();
};

/** Short zone abbreviation for a given IANA tz + instant, e.g. "CDT". */
export const tzShortLabel = (tz, at = new Date()) => {
  if (!isValidTz(tz)) return '';
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: tz, timeZoneName: 'short',
    }).formatToParts(at);
    return parts.find((p) => p.type === 'timeZoneName')?.value || '';
  } catch { return ''; }
};
